import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de interfaces.
import { IEmployee } from '../interfaces/employee.interface';

/**
 * Servicio encargado de gestionar la lógica relacionada con los empleados.
 *
 * Este servicio está disponible a nivel global (`providedIn: 'root'`), lo que garantiza
 * una única instancia en toda la aplicación.
 *
 * Generalmente se utiliza para realizar operaciones como la obtención, creación,
 * actualización y eliminación de empleados.
 */
@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  /**
   * URL base para las solicitudes relacionadas con empleados.
   * Se utiliza para realizar operaciones como obtener, crear, actualizar o eliminar empleados a través de la API.
   */
  private url: string = '/employees';

  /**
   * Constructor del servicio que inyecta `HttpClient` para realizar solicitudes HTTP a la API.
   *
   * @param http Cliente HTTP de Angular para gestionar peticiones y respuestas del servidor.
   */
  constructor(private http: HttpClient) {}

  /**
   * Método para obtener todos los empleados desde la API.
   * Realiza una solicitud HTTP para recuperar los datos y los devuelve en formato de promesa.
   *
   * @returns Una promesa que se resuelve con los datos obtenidos de la API.
   */
  getAll(): Promise<any> {
    return firstValueFrom(this.http.get(environment.apiUrl + this.url));
  }

  /**
   * Método para crear un nuevo registro de empleado en la API.
   * Realiza una solicitud HTTP POST para enviar los datos del empleado al servidor.
   *
   * @param employee - Objeto que contiene la información del empleado a ser creado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la creación del empleado.
   */
  create(employee: IEmployee): Promise<any> {
    return firstValueFrom(
      this.http.post(environment.apiUrl + this.url, {
        roleId: employee.role.id,
        username: employee.username,
        email: employee.email,
        password: employee.password,
        displayName: employee.displayName,
        disabled: employee.disabled,
      })
    );
  }

  /**
   * Método para actualizar un registro de empleado en la API.
   * Realiza una solicitud HTTP PUT para enviar los datos actualizados del empleado al servidor.
   *
   * @param employee - Objeto que contiene la información del empleado con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la actualización del empleado.
   */
  update(employee: IEmployee): Promise<any> {
    return firstValueFrom(
      this.http.put(`${environment.apiUrl + this.url}/${employee.id}`, {
        roleId: employee.role.id,
        username: employee.username,
        email: employee.email,
        displayName: employee.displayName,
        disabled: employee.disabled,
      })
    );
  }

  /**
   * Método que actualiza la contraseña de un empleado.
   *
   * @param employee - Objeto que contiene la información del empleado con los cambios a ser actualizados.
   *
   * @returns Una promesa que resuelve con la respuesta de la actualización de la contraseña.
   */
  patchPassword(employee: IEmployee): Promise<any> {
    return firstValueFrom(
      this.http.patch(
        `${environment.apiUrl}${this.url}/${employee.id}/password`,
        {
          password: employee.password,
        }
      )
    );
  }

  /**
   * Método para eliminar un registro de empleado en la API.
   * Realiza una solicitud HTTP DELETE para eliminar un empleado basado en su ID.
   *
   * @param employee - Objeto que contiene la información del empleado con los cambios a ser actualizados.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la eliminación del empleado.
   */
  delete(employee: IEmployee): Promise<any> {
    return firstValueFrom(
      this.http.delete(`${environment.apiUrl + this.url}/${employee.id}`)
    );
  }
}
